import { zodResolver } from '@hookform/resolvers/zod';
import { VisuallyHidden } from '@reach/visually-hidden';
import { navigate } from 'gatsby';
import { find, propEq } from 'ramda';
import React, { ChangeEventHandler, FC, ReactNode, forwardRef, useState } from 'react';
import { Controller, ControllerRenderProps, useForm } from 'react-hook-form';
import { useDeepCompareEffect } from 'react-use';
import styled from 'styled-components';
import { z } from 'zod';

import BasicText from '@ui/atoms/BasicText';
import Button from '@ui/atoms/Button';
import FlexColumn, { IFlexColumn } from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';
import NavigationSection from '@ui/atoms/NavigationSection';
import StyledLink from '@ui/atoms/StyledLink';

import CenteredGridItem from '@ui/molecules/CenteredGridItem';
import DashedLinkButton from '@ui/molecules/DashedLinkButton';
import SubmitButton from '@ui/molecules/SubmitButton';

import fullWidth from '@ui/styled/FullWidth';
import spaced from '@ui/styled/Spaced';

import useMemberInsuranceInfo, { IInsurance } from '@hooks/useMemberInsurance';

import { getColorFromTheme } from '@utils/theme';

import theme from '@theme/FolxTheme';

import { schema } from './InsuranceSelectionStep.data';

interface IInsuranceSelectionStepProps {
	pageContent: IInsuranceSelectionStepContent;
	existingFormData?: Record<string, unknown>;
	submit?: (id: string) => void;
	navBack: () => void;
}

export interface IInsuranceSelectionStepContent {
	title: string;
	description: string;
}

interface IInsuranceCardProps {
	insurance: IInsurance;
	value?: string;
	onChange?: ControllerRenderProps['onChange'];
	onBlur?: ChangeEventHandler<HTMLInputElement>;
	alignment?: 'start' | 'center' | 'end';
	children?: ReactNode;
	error?: string;
	name?: string;
}

interface IInsuranceCardContainerProps {
	selected: boolean;
	focused: boolean;
}

const InsuranceCardContainer = styled.button<IInsuranceCardContainerProps>`
	border: ${(props) => (props.selected ? '0.2rem' : '0.1rem')} solid #000000;
	${(props) => (props.selected ? 'box-shadow: 0rem 0.3rem 0.6rem rgba(0, 0, 0, 0.08);' : null)}
	background: ${({ selected, theme: components }) =>
		selected
			? getColorFromTheme(components.selectableButton.color, components.selectableButton.tint)
			: theme.colors.white};
	border-radius: 0.8rem;
	padding: 2.4rem;
	text-align: left;
	cursor: pointer;
	outline: ${({ focused }) => (focused ? '5px auto Highlight' : 'none')};
	outline: ${({ focused }) => (focused ? '5px auto -webkit-focus-ring-color' : 'none')};
`;

const UpdateButton = styled(Button)`
	width: auto;
	border-radius: 2.4rem;
`;

const LinkWrapper = styled('div')`
	${BasicText} {
		font-size: 1.4rem;
	}
`;

const FullWidthForm = fullWidth(spaced('form'));
const FullWidthColumn = fullWidth<IFlexColumn>(FlexColumn);
const SpacedColumn = spaced<IFlexColumn>(FullWidthColumn);

type Ref = HTMLInputElement;

const InsuranceCard = forwardRef<Ref, IInsuranceCardProps>(
	({ insurance, error, name, value, onChange = () => {}, onBlur = () => {} }, ref) => {
		const [focused, setFocused] = useState(false);

		const input = (
			<VisuallyHidden>
				<input
					type="radio"
					id={insurance.id}
					name={name}
					aria-label={`${insurance.insuranceProviderName} with ID ${insurance.memberInsuranceId}`}
					checked={value === insurance.id}
					value={insurance.memberInsuranceId}
					onFocus={() => setFocused(true)}
					onBlur={(e) => {
						setFocused(false);
						onBlur(e);
					}}
					onChange={onChange}
					ref={ref}
				/>
			</VisuallyHidden>
		);

		return (
			<InsuranceCardContainer
				tabIndex={-1}
				selected={value === insurance.id}
				focused={focused}
				key={insurance.id}
				onClick={(e) => {
					e.preventDefault();
					onChange(insurance.id);
				}}>
				<SpacedColumn gap="xs" marginBottom="md">
					<BasicText variant="small">{insurance.insuranceProviderName}</BasicText>
					<BasicText>{insurance.memberInsuranceId}</BasicText>
				</SpacedColumn>
				<UpdateButton
					labelSize="xs"
					label="Edit"
					onClick={(e) => {
						e.preventDefault();
						navigate(`../insurance_details_edit/${insurance.id}`);
					}}
				/>
				{input}
			</InsuranceCardContainer>
		);
	}
);
InsuranceCard.displayName = 'InsuranceCard';

const InsuranceSelectionStep: FC<IInsuranceSelectionStepProps> = ({
	pageContent = {},
	existingFormData,
	submit,
	navBack,
}) => {
	const [submitting, setSubmitting] = useState(false);
	const { title, description } = pageContent;

	const { memberInsurance, loading: memberInsuranceLoading } = useMemberInsuranceInfo(
		false,
		'network-only'
	);

	type Schema = Partial<z.input<typeof schema>>;

	const form = useForm<Schema>({
		defaultValues: {
			...(existingFormData as unknown as Schema),
		},
		resolver: zodResolver(schema),
		mode: 'onChange',
	});
	const {
		handleSubmit,
		control,
		setValue,
		formState: { errors },
	} = form;

	useDeepCompareEffect(() => {
		if (memberInsuranceLoading || !memberInsurance) return;

		if (memberInsurance.length === 1) {
			setValue('memberInsuranceInfo', memberInsurance[0].id);
		}
	}, [memberInsurance, memberInsuranceLoading]);
	const onSubmit = (data: Record<string, string>) => {
		setSubmitting(true);

		if (submit) {
			//	This method is passed in the insurance UUID as the member ID can be duplicated across providers entered by the user.
			//	Finding and submitting the member ID from the insurance UUID passed in is a necessary code smell
			//	until the backend tech debt to use the insurance UUID over the member ID for visit price is resolved.
			const selectedInsuranceId =
				find(propEq('id', data.memberInsuranceInfo), memberInsurance)?.memberInsuranceId ?? '';
			submit(selectedInsuranceId);
		}
		setSubmitting(false);
	};

	return (
		<CenteredGridItem>
			<SpacedColumn gap="sm" marginBottom="lg">
				<HeadingText>{title}</HeadingText>
				<BasicText>{description}</BasicText>
			</SpacedColumn>
			<FullWidthForm onSubmit={handleSubmit(onSubmit)} marginBottom="xxxl">
				<SpacedColumn gap="md" marginBottom="lg">
					{memberInsurance && (
						<Controller
							name="memberInsuranceInfo"
							control={control}
							disabled={memberInsurance.length === 1}
							render={({ field: { ...field } }) => (
								<>
									{memberInsurance.map((insurance: IInsurance) => (
										<InsuranceCard key={insurance.id} insurance={insurance} {...field} />
									))}
								</>
							)}
						/>
					)}
					{memberInsurance && memberInsurance.length > 0 ? (
						<LinkWrapper>
							<BasicText>
								＋&nbsp;
								<StyledLink to="../insurance_provider" variant="underline">
									add new plan
								</StyledLink>
							</BasicText>
						</LinkWrapper>
					) : (
						<DashedLinkButton href="../insurance_provider" label="add new plan" />
					)}
					<NavigationSection>
						<Button label="Back" onClick={() => navBack()} />
						<SubmitButton variant="secondary" label="Next" submitting={submitting} />
					</NavigationSection>
				</SpacedColumn>
			</FullWidthForm>
		</CenteredGridItem>
	);
};

export default InsuranceSelectionStep;
